import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function StreamViewingMenu({
  qualityOptions = [],
  qualityValue = "auto",
  onQualityChange,
  onPictureInPicture,
  pictureInPictureAvailable = true,
  sourceLabel = "",
  statusText = "",
}) {
  const [open, setOpen] = useState(false);
  const [compactViewport, setCompactViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 900
  );
  const [anchorRect, setAnchorRect] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onViewportChange = () => {
      setCompactViewport(window.innerWidth <= 900);
      if (rootRef.current) setAnchorRect(rootRef.current.getBoundingClientRect());
    };
    window.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener?.("resize", onViewportChange);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener?.("resize", onViewportChange);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (rootRef.current) setAnchorRect(rootRef.current.getBoundingClientRect());

    const onPointerDown = (event) => {
      if (
        rootRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const selected =
    qualityOptions.find((option) => option.value === qualityValue) ||
    qualityOptions[0] ||
    null;

  return (
    <div
      ref={rootRef}
      className="ga-viewing-menu"
      style={{
        position: "absolute",
        top: 10,
        left: 10,
        zIndex: 58,
        pointerEvents: "auto",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-label="Viewing options"
          style={{
            minHeight: 36,
            padding: "0 12px",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.24)",
            background: "rgba(0,0,0,0.78)",
            color: "#fff",
            boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
            fontSize: 12,
            fontWeight: 850,
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true">⚙️</span>
          <span>{selected?.shortLabel || selected?.label || "Viewing"}</span>
        </button>

        <button
          type="button"
          onClick={async () => {
            await onPictureInPicture?.();
          }}
          aria-label="Pop out stream"
          title="Pop out stream"
          style={{
            minHeight: 36,
            padding: "0 12px",
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.24)",
            background: "rgba(0,0,0,0.78)",
            color: "#fff",
            boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
            fontSize: 12,
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true">⧉</span>
          <span>Pop out</span>
        </button>
      </div>

      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={panelRef}
              style={{
                position: "fixed",
                zIndex: 99999,
                ...(compactViewport
                  ? {
                      left: 12,
                      right: 12,
                      top: "max(68px, calc(env(safe-area-inset-top, 0px) + 52px))",
                      bottom: "max(12px, env(safe-area-inset-bottom, 0px))",
                      width: "auto",
                      maxHeight: "none",
                    }
                  : {
                      left: Math.max(
                        12,
                        Math.min(
                          Number(anchorRect?.left || 12),
                          Math.max(12, window.innerWidth - 310)
                        )
                      ),
                      top: Math.max(
                        60,
                        Math.min(
                          Number(anchorRect?.bottom || 60) + 8,
                          Math.max(60, window.innerHeight - 440)
                        )
                      ),
                      width: 286,
                      maxHeight: "min(70vh, 420px)",
                    }),
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.16)",
                background: "rgba(8,8,12,0.985)",
                color: "#fff",
                boxShadow: "0 18px 54px rgba(0,0,0,0.65)",
                padding: 11,
                overflowY: "auto",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
              }}
            >
          <div style={{ fontSize: 13, fontWeight: 900 }}>Viewing options</div>
          {sourceLabel ? (
            <div style={{ marginTop: 2, fontSize: 10, opacity: 0.62 }}>{sourceLabel}</div>
          ) : null}

          <button
            type="button"
            onClick={async () => {
              await onPictureInPicture?.();
              setOpen(false);
            }}
            style={{
              width: "100%",
              minHeight: 44,
              marginTop: 10,
              borderRadius: 11,
              border: "1px solid rgba(120,185,255,0.24)",
              background: "linear-gradient(135deg, rgba(65,120,255,0.18), rgba(130,70,255,0.16))",
              color: "#fff",
              fontSize: 12,
              fontWeight: 950,
              cursor: "pointer",
            }}
          >
            ⧉ Pop-out / Picture-in-Picture
          </button>

          <div
            style={{
              marginTop: 12,
              fontSize: 10,
              fontWeight: 850,
              opacity: 0.65,
              letterSpacing: 0.5,
            }}
          >
            VIDEO QUALITY
          </div>

          <div style={{ display: "grid", gap: 6, marginTop: 7 }}>
            {qualityOptions.map((option) => {
              const active = option.value === qualityValue;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onQualityChange?.(option.value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    minHeight: 42,
                    padding: "8px 10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    borderRadius: 11,
                    border: active
                      ? "1px solid rgba(90,205,255,0.45)"
                      : "1px solid rgba(255,255,255,0.08)",
                    background: active
                      ? "rgba(40,150,220,0.16)"
                      : "rgba(255,255,255,0.045)",
                    color: "#fff",
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 900 }}>
                      {option.label}
                    </span>
                    {option.detail ? (
                      <span
                        style={{
                          display: "block",
                          marginTop: 2,
                          fontSize: 10,
                          opacity: 0.62,
                        }}
                      >
                        {option.detail}
                      </span>
                    ) : null}
                  </span>
                  <span style={{ opacity: active ? 1 : 0.35 }}>{active ? "✓" : ""}</span>
                </button>
              );
            })}
          </div>

          {statusText ? (
            <div
              style={{
                marginTop: 9,
                padding: "8px 9px",
                borderRadius: 9,
                background: "rgba(255,255,255,0.045)",
                fontSize: 10,
                lineHeight: 1.35,
                opacity: 0.78,
              }}
            >
              {statusText}
            </div>
          ) : null}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
